// Frustration Detection Service
// Tracks user frustration signals like rage clicks, rapid interactions, and error patterns

interface ClickData {
  timestamp: number;
  x: number;
  y: number;
  target: string;
}

interface FrustrationEvent {
  type: 'rage_click' | 'rapid_scroll' | 'form_abandonment' | 'repeated_error' | 'dead_click';
  severity: 'low' | 'medium' | 'high';
  context: {
    element?: string;
    page_path: string;
    click_count?: number;
    time_window?: number;
    error_message?: string;
    scroll_velocity?: number;
    user_segment?: string;
  };
  timestamp: number;
}

class FrustrationDetectionService {
  private static instance: FrustrationDetectionService;
  private clickHistory: ClickData[] = [];
  private scrollHistory: number[] = [];
  private errorHistory: Map<string, number> = new Map();
  private frustrationScore: number = 0;
  private sessionFrustrationEvents: FrustrationEvent[] = [];
  private lastScrollTime: number = 0;
  private deadClickTargets: Set<string> = new Set();
  
  // Thresholds for detection
  private readonly RAGE_CLICK_THRESHOLD = 3; // clicks in same area
  private readonly RAGE_CLICK_TIME_WINDOW = 1000; // milliseconds
  private readonly RAGE_CLICK_RADIUS = 50; // pixels
  private readonly RAPID_SCROLL_THRESHOLD = 5000; // pixels per second
  private readonly ERROR_REPEAT_THRESHOLD = 3; // same error count
  private readonly HIGH_VALUE_THRESHOLD = 100; // dollars for high-value user

  private constructor() {
    this.initializeListeners();
  }

  static getInstance(): FrustrationDetectionService {
    if (!FrustrationDetectionService.instance) {
      FrustrationDetectionService.instance = new FrustrationDetectionService();
    }
    return FrustrationDetectionService.instance;
  }

  private initializeListeners() {
    // Global click listener for rage click detection
    document.addEventListener('click', this.handleClick.bind(this), true);
    
    // Scroll listener for rapid scrolling detection
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    // Listen for custom error events
    window.addEventListener('error', this.handleError.bind(this));
  }

  private handleClick(event: MouseEvent) {
    const now = Date.now();
    const target = (event.target as HTMLElement);
    const targetIdentifier = this.getElementIdentifier(target);
    
    // Record click
    const clickData: ClickData = {
      timestamp: now,
      x: event.clientX,
      y: event.clientY,
      target: targetIdentifier
    };
    
    this.clickHistory.push(clickData);
    
    // Keep only recent clicks (last 5 seconds)
    this.clickHistory = this.clickHistory.filter(
      click => now - click.timestamp < 5000
    );
    
    // Check for rage clicks
    this.detectRageClicks(clickData);
    
    // Check for dead clicks (clicks on non-interactive elements)
    this.detectDeadClicks(event, target);
  }

  private detectRageClicks(currentClick: ClickData) {
    const recentClicks = this.clickHistory.filter(
      click => currentClick.timestamp - click.timestamp < this.RAGE_CLICK_TIME_WINDOW
    );
    
    // Find clicks in the same area
    const nearbyClicks = recentClicks.filter(click => {
      const distance = Math.sqrt(
        Math.pow(click.x - currentClick.x, 2) + 
        Math.pow(click.y - currentClick.y, 2)
      );
      return distance < this.RAGE_CLICK_RADIUS;
    });
    
    if (nearbyClicks.length >= this.RAGE_CLICK_THRESHOLD) {
      this.recordFrustrationEvent({
        type: 'rage_click',
        severity: nearbyClicks.length >= 5 ? 'high' : 'medium',
        context: {
          element: currentClick.target,
          page_path: window.location.pathname,
          click_count: nearbyClicks.length,
          time_window: this.RAGE_CLICK_TIME_WINDOW
        },
        timestamp: currentClick.timestamp
      });
    }
  }

  private detectDeadClicks(event: MouseEvent, target: HTMLElement) {
    // Check if click was on a non-interactive element
    const isInteractive = 
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'SELECT' ||
      target.tagName === 'TEXTAREA' ||
      target.onclick !== null ||
      target.closest('button, a, input, select, textarea, [onclick]') !== null;
    
    if (!isInteractive) {
      const targetId = this.getElementIdentifier(target);
      
      // Track dead clicks on the same element
      if (this.deadClickTargets.has(targetId)) {
        this.recordFrustrationEvent({
          type: 'dead_click',
          severity: 'low',
          context: {
            element: targetId,
            page_path: window.location.pathname
          },
          timestamp: Date.now()
        });
      } else {
        this.deadClickTargets.add(targetId);
        // Clear after 2 seconds
        setTimeout(() => this.deadClickTargets.delete(targetId), 2000);
      }
    }
  }

  private handleScroll() {
    const now = Date.now();
    const scrollY = window.scrollY;
    
    this.scrollHistory.push(scrollY);
    
    // Keep only last 10 scroll positions
    if (this.scrollHistory.length > 10) {
      this.scrollHistory.shift();
    }
    
    // Calculate scroll velocity
    if (this.lastScrollTime > 0) {
      const timeDelta = now - this.lastScrollTime;
      const scrollDelta = Math.abs(scrollY - this.scrollHistory[this.scrollHistory.length - 2] || 0);
      const velocity = (scrollDelta / timeDelta) * 1000; // pixels per second
      
      if (velocity > this.RAPID_SCROLL_THRESHOLD) {
        this.recordFrustrationEvent({
          type: 'rapid_scroll',
          severity: velocity > 10000 ? 'high' : 'medium',
          context: {
            page_path: window.location.pathname,
            scroll_velocity: velocity
          },
          timestamp: now
        });
      }
    }
    
    this.lastScrollTime = now;
  }

  private handleError(event: ErrorEvent) {
    const errorKey = `${event.message}-${event.filename}`;
    const errorCount = (this.errorHistory.get(errorKey) || 0) + 1;
    this.errorHistory.set(errorKey, errorCount);
    
    if (errorCount >= this.ERROR_REPEAT_THRESHOLD) {
      this.recordFrustrationEvent({
        type: 'repeated_error',
        severity: 'high',
        context: {
          error_message: event.message,
          page_path: window.location.pathname
        },
        timestamp: Date.now()
      });
    }
    
    // Clean up old errors after 5 minutes
    setTimeout(() => {
      this.errorHistory.delete(errorKey);
    }, 5 * 60 * 1000);
  }

  private recordFrustrationEvent(event: FrustrationEvent) {
    this.sessionFrustrationEvents.push(event);
    this.updateFrustrationScore(event);
    
    // Track in analytics
    import('./AnalyticsService.ts').then(({ default: analyticsService }) => {
      analyticsService.trackEvent('frustration_signal', {
        frustration_type: event.type,
        severity: event.severity,
        frustration_score: this.frustrationScore,
        ...event.context
      });
    });
  }

  private updateFrustrationScore(event: FrustrationEvent) {
    const scoreIncrement = {
      low: 1,
      medium: 3,
      high: 5
    };
    
    this.frustrationScore += scoreIncrement[event.severity];
    
    // Decay score over time (reduce by 1 every minute)
    setTimeout(() => {
      this.frustrationScore = Math.max(0, this.frustrationScore - 1);
    }, 60000);
  }

  private getElementIdentifier(element: HTMLElement): string {
    const id = element.id ? `#${element.id}` : '';
    
    // Safely handle className - it might be a string, DOMTokenList, or undefined
    let classes = '';
    if (element.className) {
      if (typeof element.className === 'string') {
        classes = element.className ? `.${element.className.split(' ').join('.')}` : '';
      } else if (element.className.toString) {
        // Handle DOMTokenList or other objects with toString method
        const classString = element.className.toString();
        classes = classString ? `.${classString.split(' ').join('.')}` : '';
      }
    }
    
    const tag = element.tagName.toLowerCase();
    const text = element.textContent?.slice(0, 20) || '';
    
    return `${tag}${id}${classes}[${text}]`;
  }

  // Public methods
  public getFrustrationScore(): number {
    return this.frustrationScore;
  }

  public getSessionFrustrationEvents(): FrustrationEvent[] {
    return [...this.sessionFrustrationEvents];
  }

  public isHighFrustrationUser(): boolean {
    return this.frustrationScore > 10;
  }

  public trackFormAbandonment(formName: string, fieldName: string) {
    this.recordFrustrationEvent({
      type: 'form_abandonment',
      severity: 'medium',
      context: {
        element: `${formName}.${fieldName}`,
        page_path: window.location.pathname
      },
      timestamp: Date.now()
    });
  }

  // Session replay markers for high-value users
  public markHighValueUserAction(action: string, value: number) {
    if (value >= this.HIGH_VALUE_THRESHOLD) {
      import('./AnalyticsService.ts').then(({ default: analyticsService }) => {
        analyticsService.trackEvent('high_value_user_action', {
          action_type: action,
          transaction_value: value,
          frustration_score: this.frustrationScore,
          session_quality: this.frustrationScore < 5 ? 'good' : 'poor',
          replay_marker: `hvu_${action}_${Date.now()}`,
          user_segment: value >= 200 ? 'vip' : 'high_value'
        });
      });
    }
  }

  // Mark key moments for session replay
  public markSessionReplayPoint(eventName: string, metadata: any = {}) {
    import('./AnalyticsService.ts').then(({ default: analyticsService }) => {
      analyticsService.trackEvent('session_replay_marker', {
        marker_type: eventName,
        frustration_score: this.frustrationScore,
        timestamp: Date.now(),
        page_path: window.location.pathname,
        ...metadata
      });
    });
  }

  public clearSession() {
    this.clickHistory = [];
    this.scrollHistory = [];
    this.errorHistory.clear();
    this.frustrationScore = 0;
    this.sessionFrustrationEvents = [];
    this.deadClickTargets.clear();
  }
}

export default FrustrationDetectionService;